import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { db } from '@siaedts/shared/lib/supabase';
import type { ProductCategory } from '@siaedts/shared';import { useCompareProducts } from '../context/CompareContext';
interface Product {
  id: string;
  name: string;
  slug: string;
  category: string;
  price: number;
  currency?: string;
  description: string;
  image?: string;
  featured: boolean;
  specifications: Record<string, string>;
}

interface RemoteProduct {
  id: string;
  name: string;
  slug: string;
  short_description?: string | null;
  description?: string | null;
  image_url?: string | null;
  price?: number | null;
  currency?: string;
  featured: boolean;
  category?: { name: string; image_url?: string | null } | null;
}

const fallbackCategories = [
  'All',
  'Tractors',
  'Harvesting',
  'Irrigation',
  'Planting',
  'Tillage',
  'Sprayers',
  'Accessories',
];

export const mockProducts: Product[] = [
  {
    id: '1',
    name: 'John Deere 6155R Tractor',
    slug: 'john-deere-6155r',
    category: 'Tractors',
    price: 145000,
    description: '155 HP premium row-crop tractor with advanced precision farming technology',
    featured: true,
    specifications: {
      'Engine Power': '155 HP',
      'Transmission': 'AutoQuad Plus',
      'Hydraulics': '114 L/min',
      'Weight': '7,200 kg',
    },
  },
  {
    id: '2',
    name: 'Valley Center Pivot System',
    slug: 'valley-center-pivot',
    category: 'Irrigation',
    price: 85000,
    description: 'Efficient center pivot irrigation covering up to 160 acres with smart controls',
    featured: true,
    specifications: {
      'Coverage': 'Up to 160 acres',
      'Span Length': '180 ft',
      'Control': 'Valley ICON',
      'Pressure': '35-60 PSI',
    },
  },
  {
    id: '3',
    name: 'Case IH Axial-Flow 250',
    slug: 'case-ih-axial-flow-250',
    category: 'Harvesting',
    price: 320000,
    description: 'High-capacity combine harvester with advanced grain quality monitoring',
    featured: true,
    specifications: {
      'Engine Power': '450 HP',
      'Grain Tank': '410 bu',
      'Unload Rate': '4.5 bu/sec',
      'Rotor': 'Single Rotor',
    },
  },
  {
    id: '4',
    name: 'Kubota M7-171 Tractor',
    slug: 'kubota-m7-171',
    category: 'Tractors',
    price: 125000,
    description: '168 HP utility tractor with CVT transmission and premium cab comfort',
    featured: false,
    specifications: {
      'Engine Power': '168 HP',
      'Transmission': 'KVT CVT',
      'Hydraulics': '150 L/min',
      'Weight': '6,800 kg',
    },
  },
  {
    id: '5',
    name: 'New Holland CR7.90 Combine',
    slug: 'new-holland-cr790',
    category: 'Harvesting',
    price: 295000,
    description: 'Twin Rotor combine with Dynamic Feed Roll and IntelliSense automation',
    featured: false,
    specifications: {
      'Engine Power': '430 HP',
      'Grain Tank': '350 bu',
      'Rotor': 'Twin Rotor',
      'Cleaning': 'Self-Leveling',
    },
  },
  {
    id: '6',
    name: 'Massey Ferguson 8S.265',
    slug: 'massey-ferguson-8s265',
    category: 'Tractors',
    price: 185000,
    description: '265 HP flagship tractor with Dyna-VT transmission and MF Connect',
    featured: false,
    specifications: {
      'Engine Power': '265 HP',
      'Transmission': 'Dyna-VT',
      'Hydraulics': '210 L/min',
      'Weight': '9,500 kg',
    },
  },
  {
    id: '7',
    name: 'John Deere 1775NT Planter',
    slug: 'john-deere-1775nt',
    category: 'Planting',
    price: 95000,
    description: '24-row planter with ExactEmerge and MaxEmerge 5e technology',
    featured: false,
    specifications: {
      'Rows': '24',
      'Row Spacing': '30 in',
      'Seed Capacity': '3.0 bu/row',
      'Fertilizer': 'Liquid/Dry',
    },
  },
  {
    id: '8',
    name: 'Hagie STS16 Sprayer',
    slug: 'hagie-sts16',
    category: 'Sprayers',
    price: 275000,
    description: 'Self-propelled sprayer with 1,600 gal tank and 120 ft boom',
    featured: false,
    specifications: {
      'Tank Capacity': '1,600 gal',
      'Boom Width': '120 ft',
      'Engine Power': '365 HP',
      'Application': 'Precision Ag Ready',
    },
  },
];

export function Products() {

  function formatProductPrice(price: number, currency = 'USD') {
    return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(price);
  }
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [products, setProducts] = useState<Product[]>([]);
  const [filteredProducts, setFilteredProducts] = useState<Product[]>([]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [categories, setCategories] = useState<string[]>(fallbackCategories);
  const { items, addItem, removeItem, isInCompare, canAddMore } = useCompareProducts();

  useEffect(() => {
    let active = true;

    async function loadCategories() {
      try {
        const remoteCategories = await db.categories.getAll() as unknown as ProductCategory[];
        if (active && remoteCategories.length > 0) {
          setCategories(['All', ...remoteCategories.map((category) => category.name)]);
        }
      } catch {
        if (active) setCategories(fallbackCategories);
      }
    }

    async function loadPublishedProducts() {
      try {
        const remoteProducts = await db.products.getAll({ status: 'available' });
        if (!active) return;

        const mappedProducts: Product[] = (remoteProducts as unknown as RemoteProduct[]).map((product) => ({
          id: product.id,
          name: product.name,
          slug: product.slug,
          category: product.category?.name ?? 'Accessories',
          price: product.price ?? 0,
                    currency: product.currency ?? 'USD',
          description: product.short_description ?? product.description ?? '',
          image: product.image_url ?? product.category?.image_url ?? undefined,
          featured: product.featured,
          specifications: {},
        }));
        setProducts(mappedProducts);
      } catch {
        if (active) setProducts([]);
      }
    }

    void loadCategories();
    void loadPublishedProducts();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    let filtered = products;
    
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }
    
    if (searchQuery) {
      filtered = filtered.filter(p => 
        p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        p.description.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    setFilteredProducts(filtered);
  }, [products, selectedCategory, searchQuery]);

  return (
    <>
      {/* Page Header */}
      <section className="bg-green-700 text-white py-16 sm:py-24">
        <div className="container-main">
          <div className="max-w-3xl">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4">{t('products.title')}</h1>
            <p className="text-green-100 text-lg">
              {t('products.description')}
            </p>
          </div>
        </div>
      </section>

      {/* Filters & Search */}
      <section className="py-8 bg-white border-b border-gray-200 sticky top-16 z-40">
        <div className="container-main">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Category Filter */}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Product categories">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-green-700 text-white shadow-md'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                  aria-pressed={selectedCategory === category}
                >
                  {category === 'All' ? t('products.categories.all') : t(`products.categories.${category.toLowerCase()}`, { defaultValue: category })}
                </button>
              ))}
            </div>

            {/* Search & View Toggle */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 w-full sm:w-auto">
              <div className="relative flex-1 max-w-md">
                <label htmlFor="product-search" className="sr-only">{t('common.searchProducts')}</label>
                <input
                  id="product-search"
                  type="search"
                  placeholder={t('common.searchProducts')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="input pl-10 pr-4"
                  aria-label={t('common.searchProducts')}
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              
              <div className="flex items-center space-x-2" role="group" aria-label={t('common.viewMode')}>
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'grid' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-pressed={viewMode === 'grid'}
                  aria-label={t('common.gridView')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 rounded-lg transition-colors ${
                    viewMode === 'list' ? 'bg-green-100 text-green-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                  }`}
                  aria-pressed={viewMode === 'list'}
                  aria-label={t('common.listView')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {items.length > 0 && (
        <section className="bg-white border-b border-gray-200">
          <div className="container-main py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <p className="text-sm text-gray-600">
              {items.length} products saved in your quote list.
            </p>
            <Link to="/compare" className="btn-primary text-sm px-4 py-2">
              View quote list
            </Link>
          </div>
        </section>
      )}

      {/* Products Grid/List */}
      <section className="section bg-gray-50">
        <div className="container-main">
          <div className="mb-6 flex items-center justify-between">
            <p className="text-gray-600">
              {t('common.showing')} <span className="font-semibold text-gray-900">{filteredProducts.length}</span> {t('common.of')} <span className="font-semibold text-gray-900">{products.length}</span> {t('products.showingProducts')}
            </p>
            <div className="flex items-center space-x-2">
              <label htmlFor="sort" className="text-sm text-gray-600">{t('common.sortBy')}</label>
              <select id="sort" className="input py-2 px-8 text-sm" aria-label={t('common.sortProducts')}>
                <option value="featured">{t('sort.featured')}</option>
                <option value="price-asc">{t('sort.priceAsc')}</option>
                <option value="price-desc">{t('sort.priceDesc')}</option>
                <option value="name-asc">{t('sort.nameAsc')}</option>
                <option value="name-desc">{t('sort.nameDesc')}</option>
              </select>
            </div>
          </div>

          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" role="list">
              {filteredProducts.map((product) => (
                <article key={product.id} className="card group" role="listitem">
                  <Link to={`/products/${product.slug}`} className="block">
                    <div className="aspect-video bg-gray-100 relative overflow-hidden">
                      {product.image ? <img src={product.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                        <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>}
                      {product.featured && (
                        <div className="absolute top-3 right-3">
                          <span className="badge badge-green">Featured</span>
                        </div>
                      )}
                      <div className="absolute top-3 left-3">
                        <span className="badge badge-blue">{t(`products.categories.${product.category.toLowerCase()}`, { defaultValue: product.category })}</span>
                      </div>
                    </div>
                    <div className="p-6">
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors line-clamp-1">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xl font-bold text-green-700">{formatProductPrice(product.price, product.currency)}</span>
                        <span className="text-sm text-green-700 font-medium group-hover:underline">{t('common.viewDetails')} →</span>
                      </div>
                    </div>
                  </Link>
                  <div className="px-6 pb-6 pt-0">
                    <button
                      type="button"
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (isInCompare(product.id)) {
                          removeItem(product.id);
                          return;
                        }
                        if (!canAddMore) {
                          return;
                        }
                        addItem({
                          id: product.id,
                          name: product.name,
                          slug: product.slug,
                          category: product.category,
                          price: product.price,
                          currency: product.currency,
                          description: product.description,
                          image: product.image,
                          featured: product.featured,
                          specifications: product.specifications,
                        });
                      }}
                      className={`w-full rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                        isInCompare(product.id)
                          ? 'border-green-200 bg-green-50 text-green-700'
                          : canAddMore
                            ? 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:text-green-700'
                            : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                      }`}
                    >
                          {isInCompare(product.id) ? 'Remove from quote list' : canAddMore ? 'Add to quote list' : 'Quote list full'}
                    </button>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="space-y-4" role="list">
              {filteredProducts.map((product) => (
                <article key={product.id} className="card flex flex-col sm:flex-row group" role="listitem">
                  <Link to={`/products/${product.slug}`} className="block w-full sm:w-64 aspect-video bg-gray-100 relative overflow-hidden flex-shrink-0">
                    {product.image ? <img src={product.image} alt={product.name} className="absolute inset-0 h-full w-full object-cover" /> : <div className="absolute inset-0 flex items-center justify-center text-gray-400">
                      <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    </div>}
                    {product.featured && (
                      <div className="absolute top-3 right-3">
                        <span className="badge badge-green">{t('common.featured')}</span>
                      </div>
                    )}
                    <div className="absolute top-3 left-3">
                      <span className="badge badge-blue">{t(`products.categories.${product.category.toLowerCase()}`, { defaultValue: product.category })}</span>
                    </div>
                  </Link>
                  <div className="p-6 flex flex-col justify-between sm:flex-1">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-green-700 transition-colors">
                        <Link to={`/products/${product.slug}`}>{product.name}</Link>
                      </h3>
                      <p className="text-gray-600 text-sm mb-4 line-clamp-2">{product.description}</p>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {Object.entries(product.specifications).slice(0, 3).map(([key, value]) => (
                          <span key={key} className="badge badge-gray text-xs">{key}: {value}</span>
                        ))}
                      </div>
                    </div>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100 mt-4 sm:mt-0 sm:border-t-0 sm:border-l sm:pl-6">
                      <span className="text-2xl font-bold text-green-700">{formatProductPrice(product.price, product.currency)}</span>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            if (isInCompare(product.id)) {
                              removeItem(product.id);
                              return;
                            }
                            if (!canAddMore) {
                              return;
                            }
                            addItem({
                              id: product.id,
                              name: product.name,
                              slug: product.slug,
                              category: product.category,
                              price: product.price,
                              currency: product.currency,
                              description: product.description,
                              image: product.image,
                              featured: product.featured,
                              specifications: product.specifications,
                            });
                          }}
                          className={`rounded-lg border px-3 py-2 text-sm font-medium transition-colors ${
                            isInCompare(product.id)
                              ? 'border-green-200 bg-green-50 text-green-700'
                              : canAddMore
                                ? 'border-gray-200 bg-white text-gray-700 hover:border-green-300 hover:text-green-700'
                                : 'cursor-not-allowed border-gray-200 bg-gray-100 text-gray-400'
                          }`}
                        >
                          {isInCompare(product.id) ? 'Remove' : canAddMore ? 'Add to list' : 'Full'}
                        </button>
                        <Link to={`/products/${product.slug}`} className="btn-primary text-sm py-2 px-4">
                          {t('common.viewDetails')}
                        </Link>
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}

          {filteredProducts.length === 0 && (
            <div className="text-center py-16">
              <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">{t('products.noProducts')}</h3>
              <p className="text-gray-600 mb-6">{t('products.noProductsHint')}</p>
              <button
                onClick={() => { setSelectedCategory('All'); setSearchQuery(''); }}
                className="btn-outline"
              >
                {t('common.clearFilters')}
              </button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-green-700">
        <div className="container-main text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">{t('products.ctaTitle')}</h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            {t('products.ctaDescription')}
          </p>
          <Link to="/contact" className="btn bg-white text-green-700 hover:bg-green-50 text-lg px-8 py-4">
            {t('products.requestCustomQuote')}
          </Link>
        </div>
      </section>
    </>
  );
}